import logger from '@/user/logger'
import localforage from 'localforage'

const dbName = 'COMPCON Persistent'

const storeRegistry = {
  pilot_groups: localforage.createInstance({
    name: dbName,
    storeName: 'pilot_groups',
    description: 'Stores Pilot Group data',
  }),
  pilots: localforage.createInstance({
    name: dbName,
    storeName: 'pilots',
    description: 'Stores Pilot data',
  }),
  pilot_sheets: localforage.createInstance({
    name: dbName,
    storeName: 'pilot_sheets',
    description: 'Stores Active Mode Pilot Sheet data',
  }),
  content: localforage.createInstance({
    name: dbName,
    storeName: 'content',
    description: 'Stores LCP data',
  }),
  content_collection: localforage.createInstance({
    name: dbName,
    storeName: 'content_collection',
    description: 'Stores user-published content collection data',
  }),
  campaigns: localforage.createInstance({
    name: dbName,
    storeName: 'campaigns',
    description: 'Stores Campaign data',
  }),
  campaign_collection: localforage.createInstance({
    name: dbName,
    storeName: 'campaign_collection',
    description: 'Stores Published Campaign data',
  }),
  encounters: localforage.createInstance({
    name: dbName,
    storeName: 'encounters',
    description: 'Stores Encounter data',
  }),
  active_encounters: localforage.createInstance({
    name: dbName,
    storeName: 'active_encounters',
    description: 'Stores Active Encounter data',
  }),
  encounter_archives: localforage.createInstance({
    name: dbName,
    storeName: 'encounter_archives',
    description: 'Stores Active Encounter archive data',
  }),
  npcs: localforage.createInstance({
    name: dbName,
    storeName: 'npcs',
    description: 'Stores NPC data',
  }),
  narrative: localforage.createInstance({
    name: dbName,
    storeName: 'narrative',
    description: 'Stores Narrative data',
  }),
  images: localforage.createInstance({
    name: dbName,
    storeName: 'images',
    description: 'Stores user images',
  }),
  remote_images: localforage.createInstance({
    name: dbName,
    storeName: 'remote_images',
    description: 'Stores remotely hosted image urls',
  }),
  settings: localforage.createInstance({
    name: dbName,
    storeName: 'settings',
    description: 'Stores application settings',
  }),
  v2_backup: localforage.createInstance({
    name: dbName,
    storeName: 'v2_backup',
    description: 'Stores v2 data awaiting LCP installation for re-import',
  }),
}

const Initialize = async function () {
  localforage.config({
    name: dbName,
  })
  // await convertLocalstorage();
}

const SetValue = async function (key: string, value: any) {
  return await storeRegistry['settings'].setItem(key, JSON.stringify(value))
}

const GetValue = async function (key: string): Promise<any> {
  const item = (await storeRegistry['settings'].getItem(key)) as string
  if (item == null) return null
  return JSON.parse(item)
}

const SetItem = async function (collection: string, item: any) {
  if (typeof item === 'string') {
    const sr = await storeRegistry[collection.toLowerCase()]
    sr.setItem(item, item)
    return
  }

  let save = true
  const id = item.ID ? item.ID : item.sortkey ? item.sortkey : item.id

  if (item.SaveController) {
    save = item.SaveController.IsDirty
  }

  if (!save) return

  storeRegistry[collection.toLowerCase()].setItem(id, JSON.stringify(item))
}

const GetItem = async function (collection: string, id: string) {
  const item = await storeRegistry[collection.toLowerCase()].getItem(id)
  if (item == null) return null
  return JSON.parse(item)
}

const RemoveItem = async function (collection: string, id: string) {
  return await storeRegistry[collection.toLowerCase()].removeItem(id)
}

const GetAll = async function (collection: string) {
  const output = [] as any[]
  if (!storeRegistry[collection.toLowerCase()]) return output

  await storeRegistry[collection.toLowerCase()]
    .iterate(function (value: any) {
      try {
        const obj = JSON.parse(value)
        output.push(obj)
      } catch (err) {
        logger.error('Error parsing collection item', { collection, value }, err)
      }
    })
    .catch(function (err) {
      logger.error('Error getting collection data', {}, err)
    })
  return output
}

const SetAll = async function (collection: string, items: any[]) {
  const db = await storeRegistry[collection.toLowerCase()]
  await db.clear()

  const promises = items.map(item => SetItem(collection, item))
  await Promise.all(promises)
}

const ClearAll = async function (collection: string) {
  const db = await storeRegistry[collection.toLowerCase()]
  return await db.clear()
}

const GetLength = async function (collection: string) {
  const db = await storeRegistry[collection.toLowerCase()]

  if (db) return await db.length()
  return 0
}

const GetKeys = async function (collection: string) {
  const db = await storeRegistry[collection.toLowerCase()]

  if (db) return await db.keys()
  return []
}

const AddBlob = async function (collection: string, key: string, blob: Blob) {
  const db = await storeRegistry[collection.toLowerCase()]
  return await db.setItem(key, blob)
}

const GetBlob = async function (collection: string, key: string) {
  const db = await storeRegistry[collection.toLowerCase()]
  return await db.getItem(key)
}

const GetTotalStorageSize = async function (): Promise<number> {
  let total = 0
  for (const store of Object.values(storeRegistry)) {
    await store.iterate((value: any) => {
      if (value instanceof Blob) {
        total += value.size
      } else if (typeof value === 'string') {
        total += value.length
      } else {
        total += JSON.stringify(value).length
      }
    })
  }
  return total
}

const ClearAllData = async function (): Promise<void> {
  await Promise.all(Object.values(storeRegistry).map(store => store.clear()))
  logger.info('All data cleared!')
}

export {
  storeRegistry,
  Initialize,
  SetItem,
  GetItem,
  RemoveItem,
  GetAll,
  SetAll,
  GetLength,
  GetKeys,
  AddBlob,
  GetBlob,
  ClearAll,
  SetValue,
  GetValue,
  ClearAllData,
  GetTotalStorageSize,
}
